import React from "react"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { getContrastingTextColor as getContrastingTextColorUtil } from "../utils/colorUtils"
import { getReviewDisplayData, renderStarRating } from "../utils/reviewUtils"
import { applyButtonTheme, getButtonClasses } from "../utils/themeUtils"
import { buildPublicPageUrl, buildServiceUrl, buildBookingUrl } from "../utils/urlUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

import HeaderSectionDesktop from "./HeaderSectionDesktop"
// Helper function to determine text color based on background
const getContrastingTextColor = (layout, hasBackgroundImage, theme, isInWhiteContainer = false) => {
  // If content is in a white container (like scattered_images with multiple images), use dark text
  if (isInWhiteContainer) {
    return theme?.colors?.textDark || "#000000"
  }
  // If there's a background image or it's a full image layout, use light text
  if (hasBackgroundImage || layout === "full_image" || layout.includes("image")) {
    return theme?.colors?.textLight || "#FFFFFF"
  }
  // Otherwise use dark text for light backgrounds
  return theme?.colors?.textDark || "#55433F"
}

const HeroSectionDesktop = ({ section, practice, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "full_image"

  // const textAlign = settings.textAlign || "left"
  const overlayOpacity = settings.overlayOpacity || 30
  const showCTA = settings.showCTA !== undefined ? settings.showCTA : true
  const ctaText = settings.ctaText || "Book a Discover Call"
  const ctaAction = settings.ctaAction || "booking"
  const ctaTarget = settings.ctaTarget || ""

  // Get review data from practice
  const reviewData = getReviewDisplayData(practice, settings.showReviews)
  const showReviews = settings.showReviews !== undefined ? settings.showReviews : reviewData.showReviews

  const handleCTAClick = () => {
    const scrollToLayout = (layoutId) => {
      if (!layoutId) return
      const targetElement = document.querySelector(`[id^=section-${layoutId}-]`)
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: "smooth" })
      }
    }
    switch (ctaAction) {
      case "page": {
        if (ctaTarget) {
          window.location.href = buildPublicPageUrl(ctaTarget, practice)
        }
        break
      }
      case "service":
        if (ctaTarget) {
          window.open(buildServiceUrl(ctaTarget, practice), "_blank")
        }
        break
      case "booking":
        window.open(buildBookingUrl(practice), "_blank")
        break
      case "contact":
        scrollToLayout("contact")
        break
      case "scroll":
        window.scrollBy({ top: window.innerHeight, behavior: "smooth" })
        break
      case "external":
        if (ctaTarget) {
          const url = ctaTarget.startsWith("http") ? ctaTarget : `https://${ctaTarget}`
          window.open(url, "_blank")
        }
        break
      default:
        break
    }
  }

  const renderContent = (hasBackgroundImage = false, customLayout = null, isInWhiteContainer = false) => {
    const currentLayout = customLayout || layout
    const textColor = getContrastingTextColor(currentLayout, hasBackgroundImage, theme, isInWhiteContainer)

    return (
      <>
        {/* Text Content - matching Figma specs */}
        <div className="flex w-full flex-col gap-[16px]">
          {section.headline && (
            <h1
              style={{
                fontSize: settings.headlineSize || "56px",
                fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: settings.headlineColor || textColor,
                lineHeight: "1.1",
                textAlign: settings.headingAlign || settings.textAlign || "center"
              }}>
              {section.headline}
            </h1>
          )}
          {section.subheadline && (
            <p
              className="w-full"
              style={{
                fontSize: settings.subheadlineSize || "20px",
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: settings.subheadlineColor || textColor,
                textAlign: settings.subheadingAlign || settings.textAlign || "center"
              }}>
              {section.subheadline}
            </p>
          )}
        </div>

        {/* CTA Button and Reviews - matching Figma specs */}
        <div
          className="flex w-full flex-col gap-[20px]"
          style={{
            alignItems:
              settings.ctaAlign === "center" ? "center" : settings.ctaAlign === "right" ? "flex-end" : "flex-start",
            marginTop: settings.ctaTopMargin ? `${settings.ctaTopMargin}px` : "48px"
          }}>
          {/* CTA Button */}
          {showCTA && (
            <button
              onClick={handleCTAClick}
              className={twMerge(
                "flex items-center justify-center gap-[12px] px-[40px] py-[12px] text-[16px] font-normal leading-[24px] transition-all hover:shadow-lg",
                getButtonClasses(theme?.buttonStyle)
              )}
              style={applyButtonTheme(
                {
                  backgroundColor: theme?.colors?.primary || "#BD654F",
                  color: getContrastingTextColorUtil(theme?.colors?.primary || "#BD654F", theme),
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
                },
                theme
              )}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
                e.target.style.color = getContrastingTextColorUtil(theme?.colors?.primaryDark || "#A55A45", theme)
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
                e.target.style.color = getContrastingTextColorUtil(theme?.colors?.primary || "#BD654F", theme)
              }}>
              {ctaText}
            </button>
          )}

          {/* Client Reviews - matching Figma specs */}
          {showReviews && (
            <div className="flex items-center">
              <div className="mr-2 flex h-[20px] items-start">
                {renderStarRating(reviewData.averageRating, "h-[20px] w-[20px]")}
              </div>
              <span
                className="text-[16px] font-semibold leading-[24px] underline"
                style={{ color: settings.reviewTextColor || settings.subheadlineColor || textColor }}>
                {reviewData.reviewCount} verified review{reviewData.reviewCount !== 1 ? "s" : ""}
              </span>
            </div>
          )}
        </div>
      </>
    )
  }

  // Layout-specific rendering based on Figma design
  switch (layout) {
    case "image_left":
      return (
        <section
          className={twMerge(
            "relative flex items-center justify-start",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{
            backgroundColor: theme?.colors?.bgLight || "#FFFFFF"
          }}>
          <AnimatedSection className="mx-auto w-full px-[140px] py-[108px]">
            <div className="flex h-[600px] items-center">
              {section.imageUrl || (settings.images && settings.images.length > 0) ? (
                <div
                  className="h-[600px] w-1/2 bg-cover bg-center bg-no-repeat"
                  style={{ backgroundImage: `url('${section.imageUrl || settings.images[0].url}')` }}
                />
              ) : (
                <div className="flex h-[600px] w-1/2 items-center justify-center bg-gray-200">
                  <span className="text-gray-500">No image selected</span>
                </div>
              )}
              <div className="flex w-1/2 flex-col items-start justify-center gap-[48px] px-[52px]">
                {renderContent(false)}
              </div>
            </div>
          </AnimatedSection>
        </section>
      )

    case "image_right":
      return (
        <section
          className={twMerge(
            "relative flex items-center justify-start",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{
            backgroundColor: theme?.colors?.bgLight || "#FFFFFF"
          }}>
          <AnimatedSection className="mx-auto w-full px-[140px] py-[108px]">
            <div className="flex h-[600px] items-center">
              <div className="flex w-1/2 flex-col items-start justify-center gap-[48px] px-[52px]">
                {renderContent(false)}
              </div>
              {section.imageUrl || (settings.images && settings.images.length > 0) ? (
                <div
                  className="h-[600px] w-1/2 bg-cover bg-center bg-no-repeat"
                  style={{ backgroundImage: `url('${section.imageUrl || settings.images[0].url}')` }}
                />
              ) : (
                <div className="flex h-[600px] w-1/2 items-center justify-center bg-gray-200">
                  <span className="text-gray-500">No image selected</span>
                </div>
              )}
            </div>
          </AnimatedSection>
        </section>
      )

    case "half_image_left": {
      return (
        <section
          className={twMerge("relative flex h-[728px] items-center", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor: theme?.colors?.bgLight || "#FFFFFF" }}>
          <AnimatedSection className="mx-auto w-full py-[108px]">
            <div className="flex h-[728px] items-center">
              {/* Image on the left - half width */}
              {section.imageUrl || (settings.images && settings.images.length > 0) ? (
                <div
                  className="h-[728px] w-1/2 bg-cover bg-center bg-no-repeat"
                  style={{ backgroundImage: `url('${section.imageUrl || settings.images[0].url}')` }}
                />
              ) : (
                <div className="flex h-[728px] w-1/2 items-center justify-center bg-gray-200">
                  <span className="text-gray-500">No image selected</span>
                </div>
              )}
              {/* Content on the right - half width with white background */}
              <div className="flex h-[728px] w-1/2 shrink-0 flex-col items-start justify-center bg-white px-[80px] py-[137px]">
                {renderContent(false, null, true)}
              </div>
            </div>
          </AnimatedSection>
        </section>
      )
    }

    case "half_image_right": {
      return (
        <section
          className={twMerge("relative flex h-[728px] items-center", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
          style={{ backgroundColor: theme?.colors?.bgLight || "#FFFFFF" }}>
          <AnimatedSection className="mx-auto w-full py-[108px]">
            <div className="flex h-[728px] items-center">
              {/* Content on the left - half width with white background */}
              <div className="flex h-[728px] w-1/2 shrink-0 flex-col items-start justify-center bg-white px-[80px] py-[137px]">
                {renderContent(false, null, true)}
              </div>
              {/* Image on the right - half width */}
              {section.imageUrl || (settings.images && settings.images.length > 0) ? (
                <div
                  className="h-[728px] w-1/2 shrink-0 bg-cover bg-center bg-no-repeat"
                  style={{ backgroundImage: `url('${section.imageUrl || settings.images[0].url}')` }}
                />
              ) : (
                <div className="flex h-[728px] w-1/2 shrink-0 items-center justify-center bg-gray-200">
                  <span className="text-gray-500">No image selected</span>
                </div>
              )}
            </div>
          </AnimatedSection>
        </section>
      )
    }
    case "contained_image":
      return (
        <section
          className={twMerge(
            "relative flex min-h-[700px] items-center justify-center p-8",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: theme?.colors?.bgLight || "#FFFFFF" }}>
          {/* Image container with rounded corners */}
          <div className="absolute inset-8 overflow-hidden rounded-lg">
            {section.imageUrl && (
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${section.imageUrl})` }}
              />
            )}
            <div className="absolute inset-0" style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }} />
          </div>

          {/* Content overlaid on the image */}
          <AnimatedSection className="relative z-10 mx-auto max-w-4xl px-5">
            {renderContent(true, "contained_image")}
          </AnimatedSection>
        </section>
      )

    case "full_image":
    default:
      return (
        <section
          className={twMerge(
            "relative flex min-h-[700px] items-center justify-center",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: theme?.colors?.primary || "#3B82F6" }}>
          {section.imageUrl && (
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${section.imageUrl})` }}
            />
          )}
          <div className="absolute inset-0" style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }} />

          {/* Header superpuesto para full_image */}
          <div className="absolute inset-x-0 top-0 z-20">
            <HeaderSectionDesktop
              section={{ id: "hero-header", settings: { showPromoBanner: false } }}
              practice={practice}
              theme={theme}
              highlightedField={null}
              transparent={true}
            />
          </div>

          <AnimatedSection className="relative z-10 mx-auto max-w-4xl px-5">
            {renderContent(true, "full_image")}
          </AnimatedSection>
        </section>
      )
  }
}

export default HeroSectionDesktop
